'use client';

import React, { useState } from 'react';
import { SlidersHorizontal, LayoutGrid, Map, ChevronDown, X } from 'lucide-react';

const cities = ['All Locations', 'Hammamet', 'Sousse', 'Port El Kantaoui', 'Tunis', 'Djerba', 'Monastir'];
const surfaces = ['All Surfaces', 'WPT Carpet', 'Artificial Grass', 'Acrylic'];
const amenities = ['All', 'Indoor', 'Outdoor', 'Floodlit', 'Rental Equipment', 'Stay & Play'];
const sortOptions = ['Recommended', 'Price: Low to High', 'Price: High to Low', 'Rating', 'Availability'];

export default function CourtFilters() {
  const [activeCity, setActiveCity] = useState('All Locations');
  const [activeSurface, setActiveSurface] = useState('All Surfaces');
  const [activeAmenity, setActiveAmenity] = useState('All');
  const [viewMode, setViewMode] = useState<'grid' | 'map'>('grid');
  const [sortOpen, setSortOpen] = useState(false);
  const [activeSort, setActiveSort] = useState('Recommended');
  const [priceRange, setPriceRange] = useState([20, 120]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const activeFilterCount = [
    activeCity !== 'All Locations',
    activeSurface !== 'All Surfaces',
    activeAmenity !== 'All',
  ]?.filter(Boolean)?.length;

  return (
    <div className="mb-6 space-y-4">
      {/* Top Row */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            Padel Courts in Tunisia
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">32 courts across 14 hotels · Updated live</p>
        </div>

        <div className="flex items-center gap-2">
          {/* Advanced Filters */}
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all duration-150 ${
              filtersOpen || activeFilterCount > 0
                ? 'bg-primary/10 border-primary/30 text-primary' :'bg-white border-border text-muted-foreground hover:text-foreground hover:border-primary/30'
            }`}
          >
            <SlidersHorizontal size={15} />
            Filters
            {activeFilterCount > 0 && (
              <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-semibold">
                {activeFilterCount}
              </span>
            )}
          </button>

          {/* Sort */}
          <div className="relative">
            <button
              onClick={() => setSortOpen(!sortOpen)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border bg-white text-sm font-medium text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all duration-150"
            >
              {activeSort}
              <ChevronDown size={14} />
            </button>
            {sortOpen && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-card-hover border border-border py-1 z-20 animate-fade-in">
                {sortOptions?.map((opt) => (
                  <button
                    key={`sort-${opt}`}
                    onClick={() => { setActiveSort(opt); setSortOpen(false); }}
                    className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                      activeSort === opt ? 'text-primary font-medium bg-primary/5' : 'text-foreground hover:bg-muted'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* View Mode */}
          <div className="flex rounded-xl border border-border overflow-hidden bg-white">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2.5 transition-colors ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
            >
              <LayoutGrid size={16} />
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`p-2.5 transition-colors ${viewMode === 'map' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
            >
              <Map size={16} />
            </button>
          </div>
        </div>
      </div>
      {/* Filter Chips Row */}
      <div className="flex flex-wrap gap-2">
        {/* City chips */}
        {cities?.map((city) => (
          <button
            key={`city-chip-${city}`}
            onClick={() => setActiveCity(city)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-150 ${
              activeCity === city
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'bg-white border border-border text-muted-foreground hover:text-foreground hover:border-primary/40'
            }`}
          >
            {city}
          </button>
        ))}
      </div>
      {/* Expanded Filters Panel */}
      {filtersOpen && (
        <div className="bg-white rounded-2xl border border-border p-5 shadow-card animate-slide-up">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Surface */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Surface Type</p>
              <div className="space-y-1.5">
                {surfaces?.map((s) => (
                  <button
                    key={`surf-${s}`}
                    onClick={() => setActiveSurface(s)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                      activeSurface === s ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Amenities */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Amenities</p>
              <div className="space-y-1.5">
                {amenities?.map((a) => (
                  <button
                    key={`amen-${a}`}
                    onClick={() => setActiveAmenity(a)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                      activeAmenity === a ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>

            {/* Price Range */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Price / Hour (TND)
              </p>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-mono font-medium text-foreground tabular-nums">{priceRange?.[0]} TND</span>
                  <span className="text-sm font-mono font-medium text-foreground tabular-nums">{priceRange?.[1]} TND</span>
                </div>
                <input
                  type="range"
                  min={10}
                  max={200}
                  value={priceRange?.[1]}
                  onChange={(e) => setPriceRange([priceRange?.[0], Number(e?.target?.value)])}
                  className="w-full accent-primary"
                />
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={priceRange?.[0]}
                    onChange={(e) => setPriceRange([Number(e?.target?.value), priceRange?.[1]])}
                    className="flex-1 px-3 py-2 rounded-lg border border-border text-sm font-mono text-center focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="Min"
                  />
                  <input
                    type="number"
                    value={priceRange?.[1]}
                    onChange={(e) => setPriceRange([priceRange?.[0], Number(e?.target?.value)])}
                    className="flex-1 px-3 py-2 rounded-lg border border-border text-sm font-mono text-center focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="Max"
                  />
                </div>
              </div>
            </div>

            {/* Rating */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Min. Rating</p>
              <div className="space-y-1.5">
                {['Any', '3.0+', '4.0+', '4.5+', '5.0']?.map((r) => (
                  <button
                    key={`rating-${r}`}
                    className="w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted transition-all duration-150"
                  >
                    {r === 'Any' ? 'Any Rating' : `${r} ★`}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mt-5 pt-4 border-t border-border">
            <button
              onClick={() => {
                setActiveCity('All Locations');
                setActiveSurface('All Surfaces');
                setActiveAmenity('All');
                setPriceRange([20, 120]);
              }}
              className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-destructive transition-colors"
            >
              <X size={14} />
              Clear all filters
            </button>
            <button
              onClick={() => setFiltersOpen(false)}
              className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 active:scale-95 transition-all duration-150"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
}